import { ChartTypes } from 'containers/Report/Components/TypeBtn/TypeBtn';
import { AssistantChatItemType } from 'types/chatbot/chatbot';
import { CanvasOptionType, TemplateOptionType } from 'types/report/ReportDataTypes';
import { getTranslation } from 'utils/translation';

const _AI_DEFAULT_CHAT: Omit<AssistantChatItemType, 'createdAt'> = {
  type: 'assistant',
  get content() {
    return getTranslation('message', 'CONFIRM.SYNC_BOX_AI_HELP');
  },
  get originalText() {
    return getTranslation('message', 'CONFIRM.SYNC_BOX_AI_HELP');
  },
};

const _CHART_THEMES = {
  DEFAULT: 'default',
  BASIC: 'basic',
  VERDANT_SCENT: 'verdant-scent-theme',
  OCEAN_GLEAM: 'ocean-gleam-theme',
  LAVENDER_BLOOM: 'lavender-bloom-theme',
  APPLE_LEAVES: 'apple-leaves-theme',
  FROZEN_BANANA: 'frozen-banana-theme',
  PLUM_CANDY: 'plum-candy-theme',
  DRY_FIREWOOD: 'dry-firewood-theme',
  RAINBOW: 'rainbow-theme',
  MYSTIC_DAWN: 'mystic-dawn-theme',
  ROSE_RADIANCE: 'rose-radiance-theme',
  CREPE_CAKE: 'crepe-cake-theme',
  BUBBLEGUM: 'bubblegum-theme',
  WARM_MAPLE: 'warm-maple-theme',
  URBAN_SKY: 'urban-sky-theme',
  DIVERSE_FLAVORS: 'diverse-flavors-theme',
  COTTON_CANDY: 'cotton-candy-theme',
  TROPICAL_FRUIT: 'tropical-fruit-theme',
  MARKER_MAGIC: 'marker-magic-theme',
  COLOR_CARNIVAL: 'color-carnival-theme',
  TWILIGHT_DREAMS: 'twilight-dreams-theme',
};

const _USAGE_FILTER_SUPPORTED_TYPES: Set<ChartTypes> = new Set<ChartTypes>([
  'CLUSTERED_COLUMN',
  'STACKED_COLUMN',
  'STACKED_COLUMN_100',
  'CLUSTERED_BAR',
  'STACKED_BAR',
  'STACKED_BAR_100',
  'LINE',
  'STACKED_LINE',
  'STACKED_LINE_100',
  'COMBO_CHART',
  'AREA',
  'STACKED_AREA',
  'STACKED_AREA_100',
  'PIE_CHART',
  'DOUGHNUT_CHART',
  'ROSE_CHART',
  'FUNNEL_CHART',
  'TABLE_CHART',
  'SCATTER',
  'TREEMAP',
  'CONTEXT_SELECTOR',
]);

const _COLOR_LIBRARY_LIST = [
  ['#ffffff', '#f6f6f6', '#dedede', '#919191', '#6c6c6c', '#2d2d2d', '#141414', '#000000'],
  ['#FFEFEB', '#FFC6B8', '#FFA38D', '#FF543C', '#FF0011', '#B10000', '#650004', '#330002'],
  ['#FFFAF5', '#FFDFC1', '#FFCB9A', '#FF9F46', '#FF8800', '#AF5200', '#773700', '#421F00'],
  ['#FFFDE5', '#FFF8B2', '#FFF388', '#FFED2E', '#FFCD00', '#A28F00', '#5F5400', '#2E2900'],
  ['#F7FFF0', '#DFFDC1', '#B1DA8D', '#8DCD61', '#4AAF00', '#2F7A1C', '#215113', '#11290A'],
  ['#E7FDFD', '#B5FAF9', '#15CAC3', '#00B7AC', '#009286', '#006A5F', '#004B43', '#001A17'],
  ['#C2E6FF', '#90D1FF', '#51B1FF', '#007FE6', '#0062C4', '#003F77', '#002D55', '#001324'],
  ['#FEEBFA', '#FCBAEF', '#C680B8', '#B456A1', '#A01D86', '#690055', '#460039', '#140011'],
  ['#FFE0EC', '#FFC2D9', '#FFA3C7', '#FF85B8', '#FF5A9E', '#FF0081', '#780039', '#470022'],
];

const _DEFAULT_OFFSET = 70;

const _LEGEND_POSITION = {
  bottom: 50,
  left: 'center',
};

const _GRID_BOTTOM_OFFSET = 50;
const _GRID_LEFT_RIGHT_OFFSET = 10;

// TODO: 추후 다국어 적용
const _TEMPLATE_TYPE_LIST: TemplateOptionType[] = [
  {
    get label() {
      return 'Basic';
    },
    value: 'BASIC',
  },
  {
    get label() {
      return 'KPI';
    },
    value: 'KPI',
  },
  {
    get label() {
      return 'Analysis';
    },
    value: 'ANALYSIS',
  },
  {
    get label() {
      return 'Monitoring';
    },
    value: 'MONITORING',
  },
  {
    get label() {
      return 'Report-W';
    },
    value: 'REPORT_W',
  },
  {
    get label() {
      return 'Report-H';
    },
    value: 'REPORT_H',
  },
];

const _WEB_CANVAS_TYPES: CanvasOptionType[] = [
  {
    get label() {
      return `4 : 3 ${getTranslation('resource', 'RATIO')}`;
    },
    value: '4:3',
    get description() {
      return getTranslation('resource', 'MINIMUM_RESOLUTION');
    },
    style: {
      width: 1024,
      height: 768,
    },
    icon: 'Ratio43',
  },
  {
    get label() {
      return `16 : 9 ${getTranslation('resource', 'RATIO')}`;
    },
    value: '16:9',
    get description() {
      return getTranslation('resource', 'REFERENCE_RESOLUTION');
    },
    style: {
      width: 1920,
      height: 1080,
    },
    icon: 'Ratio169',
  },
  {
    get label() {
      return `21 : 9 ${getTranslation('resource', 'RATIO')}`;
    },
    value: '21:9',
    get description() {
      return getTranslation('resource', 'MAXIMUM_RESOLUTION');
    },
    style: {
      width: 2560,
      height: 1440,
    },
    icon: 'Ratio219',
  },
];

const _PAPER_CANVAS_TYPE: CanvasOptionType[] = [
  {
    get label() {
      return getTranslation('resource', 'VERTICAL_A4');
    },
    value: 'vertical-A4',
    get description() {
      return getTranslation('resource', 'VERTICAL_A4');
    },
    style: {
      width: 794,
      height: 1123,
    },
    icon: 'A4Vertical',
  },
  {
    get label() {
      return getTranslation('resource', 'HORIZONTAL_A4');
    },
    value: 'horizontal-A4',
    get description() {
      return getTranslation('resource', 'HORIZONTAL_A4');
    },
    style: {
      width: 1123,
      height: 794,
    },
    icon: 'A4Horizontal',
  },
];

const _CHART_TYPE_LIST = [
  {
    key: 'CLUSTERED_COLUMN',
    get tooltip() {
      return getTranslation('resource', 'CLUSTERED_COLUMN');
    },
  },
  {
    key: 'STACKED_COLUMN',
    get tooltip() {
      return getTranslation('resource', 'STACKED_COLUMN');
    },
  },
  {
    key: 'STACKED_COLUMN_100',
    get tooltip() {
      return getTranslation('resource', '100_STACKED_COLUMN');
    },
  },

  {
    key: 'CLUSTERED_BAR',
    get tooltip() {
      return getTranslation('resource', 'CLUSTERED_BAR');
    },
  },
  {
    key: 'STACKED_BAR',
    get tooltip() {
      return getTranslation('resource', 'STACKED_BAR');
    },
  },
  {
    key: 'STACKED_BAR_100',
    get tooltip() {
      return getTranslation('resource', '100_STACKED_BAR');
    },
  },

  {
    key: 'LINE',
    get tooltip() {
      return getTranslation('resource', 'LINE');
    },
  },
  {
    key: 'STACKED_LINE',
    get tooltip() {
      return getTranslation('resource', 'STACKED_LINE');
    },
  },
  {
    key: 'STACKED_LINE_100',
    get tooltip() {
      return getTranslation('resource', '100_STACKED_LINE');
    },
  },

  {
    key: 'COMBO_CHART',
    get tooltip() {
      return getTranslation('resource', 'COMBO_CHART');
    },
  },

  {
    key: 'AREA',
    get tooltip() {
      return getTranslation('resource', 'AREA');
    },
  },
  {
    key: 'STACKED_AREA',
    get tooltip() {
      return getTranslation('resource', 'STACKED_AREA');
    },
  },
  {
    key: 'STACKED_AREA_100',
    get tooltip() {
      return getTranslation('resource', '100_STACKED AREA');
    },
  },

  {
    key: 'PIE_CHART',
    get tooltip() {
      return getTranslation('resource', 'PIE');
    },
  },
  {
    key: 'DOUGHNUT_CHART',
    get tooltip() {
      return getTranslation('resource', 'DOUGHNUT');
    },
  },
  {
    key: 'ROSE_CHART',
    get tooltip() {
      return getTranslation('resource', 'ROSE');
    },
  },
  {
    key: 'SUNBURST',
    get tooltip() {
      return getTranslation('resource', 'SUNBURST');
    },
  },

  {
    key: 'TABLE_CHART',
    get tooltip() {
      return getTranslation('resource', 'TABLE');
    },
  },

  {
    key: 'FUNNEL_CHART',
    get tooltip() {
      return getTranslation('resource', 'FUNNEL');
    },
  },
  {
    key: 'RADAR_CHART',
    get tooltip() {
      return getTranslation('resource', 'RADAR');
    },
  },
  {
    key: 'SCATTER',
    get tooltip() {
      return getTranslation('resource', 'SCATTER');
    },
  },
  {
    key: 'TREEMAP',
    get tooltip() {
      return getTranslation('resource', 'TREEMAP');
    },
  },
  {
    key: 'SUMMARY_CARD',
    get tooltip() {
      return getTranslation('resource', 'SUMMARY_CARD');
    },
  },
  {
    key: 'GAUGE',
    get tooltip() {
      return getTranslation('resource', 'GAUGE');
    },
  },
  {
    key: 'CONTEXT_SELECTOR',
    get tooltip() {
      return getTranslation('resource', 'CONTEXT_SELECTOR');
    },
  },
];

const _LEGEND_POSITION_LIST = [
  {
    key: 'none',
    get tooltip() {
      return getTranslation('resource', 'NONE');
    },
  },
  {
    key: 'bottom',
    get tooltip() {
      return getTranslation('resource', 'BOTTOM');
    },
  },
  {
    key: 'top',
    get tooltip() {
      return getTranslation('resource', 'TOP');
    },
  },
  {
    key: 'left',
    get tooltip() {
      return getTranslation('resource', 'LEFT');
    },
  },
  {
    key: 'right',
    get tooltip() {
      return getTranslation('resource', 'RIGHT');
    },
  },
];

const _THEME_LIST = [
  {
    value: 'VERDANT_SCENT',
    get label() {
      return getTranslation('resource', 'VERDANT_SCENT');
    },
    colors: [
      '#4ea397',
      '#22c3aa',
      '#7bd9a5',
      '#2e859a',
      '#5fbdcf',
      '#56a86c',
      '#90ae95',
      '#447461',
    ],
    backgroundColor: '#f2f2f2',
  },
  {
    value: 'OCEAN_GLEAM',
    get label() {
      return getTranslation('resource', 'OCEAN_GLEAM');
    },
    colors: [
      '#a4ceff',
      '#5a9aff',
      '#0a54ff',
      '#7fedf5',
      '#47c2ff',
      '#0398fc',
      '#1769d3',
      '#123aa6',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'LAVENDER_BLOOM',
    get label() {
      return getTranslation('resource', 'LAVENDER_BLOOM');
    },
    colors: [
      '#76a4e9',
      '#8168fd',
      '#c597ff',
      '#8231d8',
      '#9863b9',
      '#d370d6',
      '#6474c2',
      '#431daf',
    ],
    backgroundColor: '#f4f2f8',
  },
  {
    value: 'APPLE_LEAVES',
    get label() {
      return getTranslation('resource', 'APPLE_LEAVES');
    },
    colors: [
      '#ffbab8',
      '#ff848c',
      '#f73d31',
      '#c20041',
      '#d2f47d',
      '#8fe899',
      '#27be91',
      '#006970',
    ],
    backgroundColor: '#f5f5f4',
  },
  {
    value: 'FROZEN_BANANA',
    get label() {
      return getTranslation('resource', 'FROZEN_BANANA');
    },
    colors: [
      '#fff05c',
      '#ffcc00',
      '#ff942a',
      '#be5f0c',
      '#76dbf9',
      '#3db2ff',
      '#0062f5',
      '#0a3385',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'PLUM_CANDY',
    get label() {
      return getTranslation('resource', 'PLUM_CANDY');
    },
    colors: [
      '#d9c1e6',
      '#b099e5',
      '#a56ff6',
      '#4a12ba',
      '#fdc4f4',
      '#ff8fda',
      '#f84fa4',
      '#ad0058',
    ],
    backgroundColor: '#f5f5f4',
  },
  {
    value: 'DRY_FIREWOOD',
    get label() {
      return getTranslation('resource', 'DRY_FIREWOOD');
    },
    colors: [
      '#cbcbcd',
      '#a1a1a5',
      '#4e4d4b',
      '#020203',
      '#cfc1b4',
      '#ab927c',
      '#9a724f',
      '#574638',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'RAINBOW',
    get label() {
      return getTranslation('resource', 'RAINBOW');
    },
    colors: [
      '#c44545',
      '#ffa749',
      '#fdd952',
      '#6be16c',
      '#6ba9e1',
      '#003b99',
      '#992ff9',
      '#ee58a4',
    ],
    backgroundColor: '#ffffff',
  },

  {
    value: 'ROSE_RADIANCE',
    get label() {
      return getTranslation('resource', 'ROSE_RADIANCE');
    },
    colors: [
      '#fc97af',
      '#b73971',
      '#4bd2a9',
      '#a7a48b',
      '#3491d4',
      '#ef6c8b',
      '#2b9789',
      '#7cb4cc',
    ],
    backgroundColor: '#f6f8f8',
  },
  {
    value: 'CREPE_CAKE',
    get label() {
      return getTranslation('resource', 'CREPE_CAKE');
    },
    colors: [
      '#2ec7c9',
      '#b6a2de',
      '#5ab1ef',
      '#ffb980',
      '#d87a80',
      '#8d98b3',
      '#e5cf0d',
      '#97b552',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'BUBBLEGUM',
    get label() {
      return getTranslation('resource', 'BUBBLEGUM');
    },
    colors: [
      '#76e9c6',
      '#ffe897',
      '#ffb4a9',
      '#b390ff',
      '#1d72af',
      '#9ee976',
      '#2cb1bd',
      '#fc8d7d',
    ],
    backgroundColor: '#f6f6f6',
  },
  {
    value: 'WARM_MAPLE',
    get label() {
      return getTranslation('resource', 'WARM_MAPLE');
    },
    colors: [
      '#628050',
      '#d99b68',
      '#d4bba7',
      '#d66046',
      '#7a5245',
      '#919e8b',
      '#f3d999',
      '#b62e35',
    ],
    backgroundColor: '#ebe5d5',
  },
  {
    value: 'URBAN_SKY',
    get label() {
      return getTranslation('resource', 'URBAN_SKY');
    },
    colors: [
      '#121b64',
      '#c8caca',
      '#ff996d',
      '#91e9e9',
      '#e8e3b0',
      '#516491',
      '#ffb248',
      '#343d3d',
    ],
    backgroundColor: '#ebecf0',
  },
  {
    value: 'DIVERSE_FLAVORS',
    get label() {
      return getTranslation('resource', 'DIVERSE_FLAVORS');
    },
    colors: [
      '#c9ef3c',
      '#ff8073',
      '#5299ff',
      '#f5de62',
      '#66d483',
      '#fdad0e',
      '#0fb11a',
      '#fa566c',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'COTTON_CANDY',
    get label() {
      return getTranslation('resource', 'COTTON_CANDY');
    },
    colors: [
      '#f6d0f9',
      '#91e9d3',
      '#b5aefd',
      '#72c2fc',
      '#6c77d8',
      '#f4a4c4',
      '#54dec7',
      '#dfa4f4',
    ],
    backgroundColor: '#f5f4f6',
  },
  {
    value: 'TROPICAL_FRUIT',
    get label() {
      return getTranslation('resource', 'TROPICAL_FRUIT');
    },
    colors: [
      '#d96aba',
      '#f29f05',
      '#f97224',
      '#97bf04',
      '#bb0000',
      '#ffcc14',
      '#f96b8c',
      '#c9d081',
    ],
    backgroundColor: '#ffffff',
  },
  {
    value: 'MARKER_MAGIC',
    get label() {
      return getTranslation('resource', 'MARKER_MAGIC');
    },
    colors: [
      '#70d6ff',
      '#ff70a6',
      '#ff9770',
      '#ffd670',
      '#e9ff70',
      '#69aefc',
      '#65df77',
      '#7af5d0',
    ],
    backgroundColor: '#f6f5f4',
  },
  {
    value: 'MYSTIC_DAWN',
    get label() {
      return getTranslation('resource', 'MYSTIC_DAWN');
    },
    colors: [
      '#898fa9',
      '#9765a0',
      '#f5dd8c',
      '#7a91f3',
      '#cb72e4',
      '#1942bc',
      '#bfa0d5',
      '#faa440',
    ],
    backgroundColor: '#484851',
  },
  {
    value: 'COLOR_CARNIVAL',
    get label() {
      return getTranslation('resource', 'COLOR_CARNIVAL');
    },
    colors: [
      '#26547c',
      '#ef476f',
      '#ffd166',
      '#06d6a0',
      '#feddcd',
      '#03045e',
      '#ff9914',
      '#14b602',
    ],
    backgroundColor: '#3d3d49',
  },
  {
    value: 'TWILIGHT_DREAMS',
    get label() {
      return getTranslation('resource', 'TWILIGHT_DREAMS');
    },
    colors: [
      '#e97699',
      '#1d2baf',
      '#97a8ff',
      '#90d7ff',
      '#d197ff',
      '#7752cb',
      '#616ee5',
      '#c7439e',
    ],
    backgroundColor: '#4c4c57',
  },
];

const _DEFAULT_CANVAS_SIZE = {
  width: 1920,
  height: 1080,
};

const _FIELD_RESOURCE_TYPE: Record<
  ChartTypes,
  {
    labels?: string;
    values?: string;
    subValues?: string;
  }
> = {
  AREA: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get subValues() {
      return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
    },
  },
  CLUSTERED_BAR: {
    get labels() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get values() {
      return getTranslation('resource', 'X_AXIS');
    },
    get subValues() {
      return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'X_AXIS')}`;
    },
  },
  CLUSTERED_COLUMN: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get subValues() {
      return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
    },
  },
  STACKED_COLUMN: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  STACKED_COLUMN_100: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  COMBO_CHART: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get subValues() {
      return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
    },
  },
  STACKED_BAR: {
    get labels() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get values() {
      return getTranslation('resource', 'X_AXIS');
    },
  },
  STACKED_BAR_100: {
    get labels() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get values() {
      return getTranslation('resource', 'X_AXIS');
    },
  },
  LINE: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
    get subValues() {
      return `${getTranslation('resource', 'SUB_AXIS')} ${getTranslation('resource', 'Y_AXIS')}`;
    },
  },
  STACKED_LINE: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  STACKED_LINE_100: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  PIE_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  DOUGHNUT_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  ROSE_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  RADAR_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  SUNBURST: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  STACKED_AREA: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  STACKED_AREA_100: {
    get labels() {
      return getTranslation('resource', 'X_AXIS');
    },
    get values() {
      return getTranslation('resource', 'Y_AXIS');
    },
  },
  SCATTER: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  TREEMAP: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  GAUGE: {
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  TABLE_CHART: {
    get labels() {
      return getTranslation('resource', 'COLUMNS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  MATRIC_TABLE: {
    get labels() {
      return getTranslation('resource', 'COLUMNS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  SUMMARY_CARD: {
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  FUNNEL_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  GEO_CHART: {
    get labels() {
      return getTranslation('resource', 'LABELS');
    },
    get values() {
      return getTranslation('resource', 'VALUES');
    },
  },
  CONTEXT_SELECTOR: {
    get labels() {
      return getTranslation('resource', 'FIELD');
    },
  },
};

export default {
  get USAGE_FILTER_SUPPORTED_TYPES() {
    return _USAGE_FILTER_SUPPORTED_TYPES;
  },
  get AI_DEFAULT_CHAT() {
    return _AI_DEFAULT_CHAT;
  },
  get CHART_THEMES() {
    return _CHART_THEMES;
  },
  get DEFAULT_OFFSET() {
    return _DEFAULT_OFFSET;
  },
  get LEGEND_POSITION() {
    return _LEGEND_POSITION;
  },
  get GRID_BOTTOM_OFFSET() {
    return _GRID_BOTTOM_OFFSET;
  },
  get GRID_LEFT_RIGHT_OFFSET() {
    return _GRID_LEFT_RIGHT_OFFSET;
  },
  get TEMPLATE_TYPE_LIST() {
    return _TEMPLATE_TYPE_LIST;
  },
  get WEB_CANVAS_TYPES() {
    return _WEB_CANVAS_TYPES;
  },
  get PAPER_CANVAS_TYPE() {
    return _PAPER_CANVAS_TYPE;
  },
  get CHART_TYPE_LIST() {
    return _CHART_TYPE_LIST;
  },
  get LEGEND_POSITION_LIST() {
    return _LEGEND_POSITION_LIST;
  },
  get THEME_LIST() {
    return _THEME_LIST;
  },
  get DEFAULT_CANVAS_SIZE() {
    return _DEFAULT_CANVAS_SIZE;
  },
  get COLOR_LIBRARY_LIST() {
    return _COLOR_LIBRARY_LIST;
  },
  get FIELD_RESOURCE_TYPE() {
    return _FIELD_RESOURCE_TYPE;
  },
};
